import { useMutation, useQuery } from '@tanstack/react-query'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'

export interface TokenResp {
  token: string
}

export interface ApiKeySummary {
  id: number
  truncated_token: string
  scope: string
  revoked: boolean
  created_at_unix: number
  last_used_at_unix: number | null
}

const getBase = (workspaceUuid: string | undefined) =>
  workspaceUuid ? `/api/workspace/${workspaceUuid}/api-key` : null

export const useGenerateApiKey = () => {
  const { currentWorkspace } = useWorkspaceContext()
  return useMutation<TokenResp, Error, { expiresMinutes: number | undefined }>({
    mutationFn: payload => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.post<TokenResp>(`${base}/`, {
        expires_minutes: payload.expiresMinutes
      })
    }
  })
}

export const useApiKeys = () => {
  const { currentWorkspace } = useWorkspaceContext()
  const base = getBase(currentWorkspace?.uuid)
  return useQuery<ApiKeySummary[], Error>({
    queryKey: ['apiKeys', currentWorkspace?.uuid],
    queryFn: () => {
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.get<ApiKeySummary[]>(`${base}/`)
    },
    enabled: !!base
  })
}

export const useRevokeApiKey = () => {
  const { currentWorkspace } = useWorkspaceContext()
  return useMutation<void, Error, { tokenId: number }>({
    mutationFn: payload => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.post(`${base}/${payload.tokenId}/revoke`)
    }
  })
}

export const useDeleteApiKey = () => {
  const { currentWorkspace } = useWorkspaceContext()
  return useMutation<void, Error, { tokenId: number }>({
    mutationFn: payload => {
      const base = getBase(currentWorkspace?.uuid)
      if (!base) throw new Error('Workspace not found')
      return fetchWrapper.delete(`${base}/${payload.tokenId}`)
    }
  })
}
